import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';

import { stepDotsFor, stepLabel as stepLabelFor } from '../constants';
import type { StepId, WizardState } from '../types';
import { ProgressBar } from './ProgressBar';
import { PreviewSidebar } from './PreviewSidebar';

interface Props {
  state: WizardState;
  children: ReactNode;
  /** Disable the Next button (e.g. step invalid). */
  nextDisabled?: boolean;
  /** Override "Next" label — used on action-firing steps. */
  nextLabel?: string;
  /** Show a spinner inside Next (mutation in flight). */
  nextPending?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onClose: () => void;
}

export function EditorShell({
  state,
  children,
  nextDisabled,
  nextLabel,
  nextPending,
  onBack,
  onNext,
  onClose,
}: Props) {
  const { t } = useTranslation('avatars');
  const haptics = useHaptics();
  const dots = stepDotsFor(state.sourceMode);
  const stepIdx = dots.findIndex(d => d.id === effectiveDotStep(state.currentStep, dots));
  const stepCount = dots.length;
  const stepLabel = stepLabelFor(t, stepIdx >= 0 ? dots[stepIdx].id : 'confirm', state.sourceMode);

  return (
    <div className="flex h-dvh flex-col bg-bg">
      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-4 pt-4">
        <Button
          variant="icona"
          size={32}
          tono="pieno"
          aria-label={t('editor.shell.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X aria-hidden className="h-4 w-4" />
        </Button>
        <div className="flex flex-1 flex-col items-center">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
            {t('editor.shell.stepOf', { current: stepIdx + 1, total: stepCount, label: stepLabel })}
          </p>
        </div>
        <div className="w-8" aria-hidden />
      </header>

      <div className="px-4 pt-3 pb-1">
        <ProgressBar current={state.currentStep} sourceMode={state.sourceMode} />
      </div>

      {/* Body: two-column on wide viewports, stacked on mobile */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-4 md:flex-row">
        <main className="flex flex-1 flex-col gap-4">{children}</main>
        <PreviewSidebar
          state={state}
          className="hidden h-fit w-64 shrink-0 md:flex"
        />
      </div>

      {/* Sticky footer */}
      <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <button
          type="button"
          onClick={() => { haptics.impact('light'); onBack?.(); }}
          disabled={!onBack}
          className={cn(
            'flex items-center gap-1 rounded-xl bg-bg-secondary px-3 py-3 text-sm font-semibold transition-opacity active:opacity-80',
            !onBack && 'opacity-30',
          )}
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          {t('common.back')}
        </button>
        <Button
          className="flex-1 gap-1"
          onClick={() => { haptics.impact('medium'); onNext?.(); }}
          disabled={!onNext || nextDisabled || nextPending}
        >
          {nextPending ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <>
              <span>{nextLabel ?? t('editor.shell.next')}</span>
              <ChevronRight aria-hidden className="h-4 w-4" />
            </>
          )}
        </Button>
      </footer>
    </div>
  );
}

function effectiveDotStep(s: StepId, dots: ReadonlyArray<{ id: StepId }>): StepId {
  if (s !== 'confirm') return s;
  return dots[dots.length - 1].id;
}
